import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Grid from '@mui/material/Grid';
import Fab from '@mui/material/Fab';
import { GridActionsCellItem } from '@mui/x-data-grid';

import CachedIcon from '@mui/icons-material/Cached';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import PendingIcon from '@mui/icons-material/Pending';
import AddIcon from '@mui/icons-material/Add';

import PageContainer from '../../component/PageContainer';
import HeaderPage from '../../component/HeaderPage';
import SmallHeaderPage from '../../component/SmallHeaderPage';
import SearchBox from '../../component/SearchBox';
import HeaderPageText from '../../component/HeaderPageText';
import DataTable from '../../component/DataTable';
import ShowOpenedServiceRequest from '../ShowOpenServiceRequest';
import NewServiceRequest from '../NewServiceRequest';

import { apiFetchOpenedServiceRequest, apiHandleSaveNewRS, apiFetchAssets, apiFetchTypesOfService } from '../../api/axios';
import { loadOpenedServiceRequests, loadAssets, loadTypesOfService } from '../../store/slice/homeSlice';
import { decryptPassword } from '../../utils/crypto';
import { buildEncodedToken } from '../../utils/auth';
import { filterList } from '../../utils/filterList';
import { isNewRSBlocked } from '../../utils/newServiceRequestPolicy';
import useAsyncAction from '../../hooks/useAsyncAction';

const OpenedServiceRequest = ({ isLoading, setIsLoading }) => {
  
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const home = useSelector((state) => state.home);
  const [initial, setInitial] = useState(true);
  const [rs, setRs] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');
  const [rsOpen, setRsOpen] = useState(null);
  const [openRS, setOpenRS] = useState(false);
  const [newSR, setNewSR] = useState(false);
  const [retSend, setRetSend] = useState('');

  const runAction = useAsyncAction(setErrorMsg);

  const newRSBlocked = isNewRSBlocked(auth.company);

  const getEncodedToken = useCallback(async () => {
    const password = await decryptPassword(auth.password, auth.user);
    return buildEncodedToken(auth.user, password);
  }, [auth.password, auth.user]);

  const columnsTable = useMemo(() => [
    { field: 'actions', type: 'actions',
        getActions: (params) => [
        <GridActionsCellItem
            label='Avaliar'
            onClick={() => handleOpenRs(params.row)}
            icon={
            <PendingIcon
            color={params.row.status.includes('Aguardando')? 'success': 'primary'}
            variant='contained'
            />}
        />,
        ]
    },
    { field: 'number', headerName: 'Número', width: 130 },
    { field: 'openingDate',headerName: 'Data de Abertura', width: 150 },
    { field: 'serviceType', headerName: 'Tipo do Serviço', width: 200 },
    { field: 'department', headerName: 'Departamento', width: 250 },
    { field: 'asset', headerName: 'Bem', width: 300 },
    { field: 'status', headerName: 'Status', width: 250 },
    { field: 'performer', headerName: 'Executante', width: 300 },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  useEffect(() => {

    fetchData();

  }, []);

  useEffect(() => {

    setRs(home.openedServiceRequests);

  }, [home.openedServiceRequests]);

  const fetchAssets = useCallback(async () => {
    await runAction(async () => {
      const encodedToken = await getEncodedToken();
      const response = await apiFetchAssets(encodedToken, auth);
      dispatch(loadAssets({assets: response.data.items}));
    }, 'Não foi possível consultar o servidor(Bens)');
  }, [auth, dispatch, getEncodedToken, runAction]);

  const fetchTypesOfService = useCallback(async () => {
    await runAction(async () => {
      const encodedToken = await getEncodedToken();
      const response = await apiFetchTypesOfService(encodedToken, auth);
      dispatch(loadTypesOfService({typesOfService: response.data.items}));
    }, 'Não foi possível consultar o servidor(Tipos de Serviço)');
  }, [auth, dispatch, getEncodedToken, runAction]);

  const fetchOpenedServiceRequest = useCallback(async () => {
    await runAction(async () => {
      const encodedToken = await getEncodedToken();
      const response = await apiFetchOpenedServiceRequest(encodedToken, auth);
      dispatch(loadOpenedServiceRequests({openedServiceRequests: response.data.items}));
    }, 'Não foi possível consultar o servidor(SS Aberta)');
  }, [auth, dispatch, getEncodedToken, runAction]);

  const fetchData = useCallback(async () => {

    setIsLoading(() => true);

    await Promise.all([
      !initial || home.assets.length <= 0 ? fetchAssets() : Promise.resolve(),
      !initial || home.typesOfService.length <= 0 ? fetchTypesOfService() : Promise.resolve(),
    ]);

    await fetchOpenedServiceRequest();

    setInitial(() => false);

    setIsLoading(() => false);

  }, [fetchAssets, fetchTypesOfService, fetchOpenedServiceRequest, initial, home.assets.length, home.typesOfService.length, setIsLoading]);

  const handleOpenRs = (rs) => {

    setRsOpen(() => rs);

    setOpenRS(() => true);

  };

  const handleCloseRs = () => setOpenRS(() => false);

  const handleFilter = (event) => {
    setRs(filterList(event.target.value, home.openedServiceRequests, ['number', 'openingDate', 'asset', 'serviceType', 'department']));
  };

  const handleOpenNewRS = () => {

    if(newRSBlocked){
        setErrorMsg('Sua empresa/filial não está habilitada para abrir novas solicitações de serviço.');
        return;
    };

    const qtdRs = home.requestedServices.length;

    if(qtdRs){
        const text = qtdRs === 1? 'avaliação pendente' : 'avaliações pendentes';
        setErrorMsg(`Você possui ${qtdRs} ${text}. Acesse a aba "Avaliações" para concluí-las antes de abrir uma nova SS.`);
        return;
    };
    
    setNewSR(() => true)

  };

  const handleCloseNewRS = () => setNewSR(() => false);

  const handleSaveNewRS = async (form) => {

    handleCloseNewRS();
    setIsLoading(() => true);

    await runAction(async () => {
      const encodedToken = await getEncodedToken();
      const response = await apiHandleSaveNewRS(form, encodedToken, auth);

      if(response.data.number){
        setRetSend(() => `SS gerada ${response.data.number}`);
        await fetchOpenedServiceRequest();
      }else{
        setErrorMsg('Servidor não retornou o número da SS criada');
      }
    }, 'Não foi possível criar a SS');

    setIsLoading(() => false);

  };

  if(!home || ! home.openedServiceRequests || !auth){
    return <Box></Box>;
    
  };

  return (

    <PageContainer>

      <NewServiceRequest
        newSR={newSR}
        handleCloseNewRS={handleCloseNewRS}
        handleSaveNewRS={handleSaveNewRS}
      />

      <ShowOpenedServiceRequest
        open={openRS}
        srToShow={rsOpen}
        handleCloseRs={handleCloseRs}      
      />

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} 
        open={retSend !== null && retSend !== undefined && retSend.trim() !== ''} autoHideDuration={3000} onClose={() => setRetSend('')}>
        <Alert 
          severity='success'
          onClose={() => setRetSend('')}>
          {retSend}
        </Alert>
      </Snackbar>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} 
        open={errorMsg !== null && errorMsg !== undefined && errorMsg.trim() !== ''} autoHideDuration={5000} onClose={() => setErrorMsg('')}>
        <Alert 
          severity='error'
          onClose={() => setErrorMsg('')}>
          {`Ops! ${errorMsg}`}
        </Alert>
      </Snackbar>
 
      <Box component='section' sx={{display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%'}}>
        
          <SmallHeaderPage>

            <HeaderPageText
              text= {`(${home.openedServiceRequests.length})`}
              icon={<PendingActionsIcon/>}
            />
            
            <SearchBox
              label='Filtrar...'
              handleFilter={handleFilter}
              isLoading={isLoading}
            />
            
            <Box sx={{display: 'flex', 
                flexDirection: 'column', 
                minHeight: '30vh', 
                justifyContent: 'space-evenly', 
                position: 'absolute', 
                bottom: 30, right: 30, bgcolor: 'transparent'}} >

              <Tooltip describeChild title='Recarregar solicitações de serviço'>
                <Fab size='medium' color='primary' onClick={fetchData}>
                  <CachedIcon sx={{ color: 'white'}}/>
                </Fab>
              </Tooltip>

              {!newRSBlocked &&
                <Tooltip describeChild title='Abrir nova solicitação de serviço'>
                  <Fab size='medium' color='primary' onClick={handleOpenNewRS}>
                    <AddIcon sx={{ color: 'white'}}/>
                  </Fab>
                </Tooltip>
              }

            </Box>

          </SmallHeaderPage>


          <HeaderPage>

            <Grid container>

              <Grid item sm={4} sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center'}}>

                <HeaderPageText
                  text= {`Abertas (${home.openedServiceRequests.length})`}
                  icon={<PendingActionsIcon/>}
                />

              </Grid>

              <Grid item sm={4} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center'}}>

                <SearchBox
                  label='Filtrar...'
                  handleFilter={handleFilter}
                  isLoading={isLoading}
                />

              </Grid>

              <Grid item sm={4} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center'}}>
              
                <ButtonGroup variant='contained' disabled={isLoading}>
                  <Tooltip describeChild title='Recarregar solicitações de serviço'>
                    <Button
                      startIcon={<CachedIcon/>} 
                      onClick={fetchData}>
                      Recarregar
                    </Button>
                  </Tooltip>

                  {!newRSBlocked &&
                    <Tooltip describeChild title='Abrir nova solicitação de serviço'>
                      <Button startIcon={<AddIcon/>} onClick={handleOpenNewRS}>Nova</Button>
                    </Tooltip>
                  }
                </ButtonGroup>

              </Grid>

            </Grid>

          </HeaderPage>

          <DataTable
            data={rs || []}
            isLoading={isLoading}
            columnsTable={columnsTable}
            pageSize={6}
            rowsPerPageOptions={6}
            fieldId='number'
            handleOpen={handleOpenRs}
          />
        
      </Box>
      
    </PageContainer>

  );
};

export default OpenedServiceRequest;